import { CURRENCY_SYMBOL, ORDER_STATUS } from 'utils/transform';
import { priceFromAPI } from 'utils/price';
import { STATUS_PROCESSED, STATUS_PAID } from 'pages/Payouts/helpers';
import { STATUS_REFUNDED } from 'pages/Transactions/helpers';
import { getCountryObject } from 'utils/phoneUtils';
import { CUSTOM_DISPUTE_STATUSES } from 'pages/Disputes/helpers';
import { FUNDRAISER_PRODUCT, REGULAR_PRODUCT } from 'services/api/types';

export const isNonFundraiserProduct = (item) => item.type !== FUNDRAISER_PRODUCT && item.type !== REGULAR_PRODUCT;

export const rejectValue = (errorResponse) => {
  if (!errorResponse?.data) return errorResponse;
  const data = !!errorResponse.data.errors
    ? { message: Object.values(errorResponse.data.errors)?.[0] }
    : errorResponse.data;
  return {
    status: errorResponse.status,
    message: data.message,
    skipNotification: errorResponse.skipNotification || false,
  };
};
export const isRejectedAction = (action) => {
  return action.type.endsWith('rejected');
};

export const flatRegularProduct = (data) => {
  return {
    price: priceFromAPI(data.productable.data.price),
    currency_symbol:
      CURRENCY_SYMBOL[data.productable.data.currency] || CURRENCY_SYMBOL['EUR'],
    auto_accept_orders: data.productable.data.auto_accept_orders,
    available_stock: data.productable.data.available_stock,
    has_stock_limit: data.productable.data.has_stock_limit,
    request_delivery_details: data.productable.data.request_delivery_details,
    tax: data.productable.data.tax,
    video_embed: data.productable.data.video_embed,
  };
};

export const flatFundRaisingProduct = (data) => {
  return {
    price: priceFromAPI(data.productable.data.price),
    currency_symbol:
      CURRENCY_SYMBOL[data.productable.data.currency] || CURRENCY_SYMBOL['EUR'],
    amount_options: data.productable.data.amount_options.map((item) => ({
      ...item,
      amount: priceFromAPI(item.amount),
    })),
    goal: priceFromAPI(data.productable.data.goal),
    raised: priceFromAPI(data.productable.data.raised),
    video_embed: data.productable.data.video_embed,
  };
};

export const flatCartProduct = (data) => {
  const clonedObj = Object.assign(
    {},
    {
      ...data,
      productable: data.product.data?.productable?.data,
      priceToDisplay: priceFromAPI(data.price),
      type: data.product.data.productable_type,
    }
  );
  delete clonedObj.product.data?.productable;

  return {
    ...clonedObj,
    product: clonedObj.product.data,
  };
};

export const buildFromCartMeta = (data, meta) => {
  const payload = {
    subtotal: priceFromAPI(meta.discounted_total),
    discountApplied: priceFromAPI(meta.total - meta.discounted_total),
    total: priceFromAPI(meta.total),
    shippingCost: priceFromAPI(0),
    tax: priceFromAPI(meta.tax),
    userPaysFees: meta.wants_to_pay_fees,
    fees: priceFromAPI(meta.transaction_fees),
    charge: priceFromAPI(meta.charge),
    initialCharge: meta.charge,
    comments: meta.order_comments,
    isAnonymous: meta.is_anonymous,
    currencySymbol: meta.currency
  };

  return payload;
};

export const flatOrder = (data) => {
  return {
    orderProducts: data.orderProducts?.data || [],
    status: ORDER_STATUS[data.status] || (data.status || '').toUpperCase(),
    total: priceFromAPI(data.total),
    currency_symbol: CURRENCY_SYMBOL[data.currency],
    product_name: data.product_info?.name || '-',
    tax: priceFromAPI(data.tax),
    fees: priceFromAPI(data.transaction_fees),
    charge: priceFromAPI(data.total + data.transaction_fees),
    subtotal: priceFromAPI(data.discounted_price_tax_excl),
  };
};

export const flatCalculateData = (data) => {
  return {
    priceToDisplay: priceFromAPI(data.total),
  };
};
export const flatCalculateMeta = (meta) => {
  return {
    total: priceFromAPI(meta.order_price),
    fees: priceFromAPI(meta.user_facing_fees),
    charge: priceFromAPI(meta.total),
  };
};

export const flatTransaction = (transaction) => {
  return {
    ...transaction,
    txtPayFee: `${CURRENCY_SYMBOL[transaction.currency]}${priceFromAPI(0)}`,
    txtPayFeeRebate: `${CURRENCY_SYMBOL[transaction.currency]}${priceFromAPI(
      0
    )}`,
    refundableAmount: priceFromAPI(
      transaction.amount - transaction.refunded_amount
    ),
    refundableAmountToDisplay: `${CURRENCY_SYMBOL[transaction.currency]
      }${priceFromAPI(transaction.amount - transaction.refunded_amount)}`,
    amountToDisplay: `${CURRENCY_SYMBOL[transaction.currency]}${priceFromAPI(
      transaction.amount
    )}`,
    netBalanceToDisplay: `${CURRENCY_SYMBOL[transaction.currency]
      }${priceFromAPI(transaction.net_amount)}`,
    processingFee: `${CURRENCY_SYMBOL[transaction.currency]}${priceFromAPI(
      transaction.xyzpay_processing_fee
    )}`,
    schemeFee: `${CURRENCY_SYMBOL[transaction.currency]}${priceFromAPI(
      transaction.scheme_fee
    )}`,
    company: {},
    events: [],
    chargebackFees: [],
    feeStructure: {},
    link: {},
  };
};

export const flatTransactionDetail = (transaction) => {
  const shopper_country_iso2 = getCountryObject(
    `+${transaction.shopper_phone_country}${transaction.shopper_phone}`
  )?.code?.toLowerCase();

  return {
    ...transaction,
    ...flatTransaction(transaction),
    txtPayCompanyFee: !!transaction.link ? transaction.link.company_fee : 0,
    txtPayJavelinFee: !!transaction.link ? transaction.link.txtpay_fee : 0,
    isBusinessCard: !!transaction.is_business,
    company: transaction.company?.data || {},
    events: transaction.events?.data || [],
    chargebackFees: transaction.chargebackFees?.data
      ? transaction.chargebackFees.data.map(flatChargebackFee)
      : [],
    feeStructure: transaction.feeStructure?.data || {},
    link: transaction.link?.data || {},
    shouldChargeTxtpayFees:
      !!transaction.link && !transaction.link.data.no_txtpay_fee,
    shopper_country_iso2,
  };
};
export const flatTransfer = (transfer) => {
  return {
    ...transfer,
    amountToDisplay: `${CURRENCY_SYMBOL[transfer.currency]}${priceFromAPI(
      transfer.amount
    )}`,
  };
};
export const flatAction = (action) => {
  return {
    ...action,
    user: action.user?.data || {},
  };
};
export const flatChargebackFee = (chargebackFee) => {
  return {
    ...chargebackFee,
    amountToDisplay: `${CURRENCY_SYMBOL[chargebackFee.currency]}${priceFromAPI(
      chargebackFee.amount
    )}`,
  };
};
export const flatRefund = (refund) => {
  return {
    ...refund,
    amountToDisplay: `${CURRENCY_SYMBOL[refund.currency]}${priceFromAPI(
      refund.amount
    )}`,
    feeToDisplay: `${CURRENCY_SYMBOL[refund.currency]}${priceFromAPI(
      refund.processing_fee
    )}`,
    status:
      refund.status === STATUS_PROCESSED.toLowerCase()
        ? STATUS_REFUNDED
        : refund.status,
  };
};
export const flatPayout = (payout) => {
  return {
    ...payout,
    status: payout.status === STATUS_PROCESSED ? STATUS_PAID : payout.status,
    company: payout.company.data,
    amountToDisplay: `${CURRENCY_SYMBOL[payout.currency]}${priceFromAPI(
      payout.amount
    )}`,
    creditedAmountToDisplay: `${CURRENCY_SYMBOL[payout.currency]}${priceFromAPI(
      payout.credited_amount
    )}`,
    debitedAmountToDisplay: `${CURRENCY_SYMBOL[payout.currency]}${priceFromAPI(
      payout.debited_amount
    )}`,
    grossAmountToDisplay: `${CURRENCY_SYMBOL[payout.currency]}${priceFromAPI(
      payout.gross_amount
    )}`,
    processingFee: `${CURRENCY_SYMBOL[payout.currency]}${priceFromAPI(
      payout.xyzpay_processing_fee
    )}`,
    adjustmentAmountToDisplay: `${CURRENCY_SYMBOL[payout.currency]
      }${priceFromAPI(payout.adjustment_amount)}`,
  };
};
export const flatClient = (client) => ({
  ...client,
  integration: client.integration?.data?.name || '-',
  company: client.liableCompany?.data?.name || '-',
  defaultRole: client.defaultRole?.data?.name || '-',
  defaultFeeStructure: client.defaultFeeStructure?.data?.name || '-',
});

export const flatClientDetails = (client) => ({
  ...client,
  loginUrl: client.login_url,
  homepageUrl: client.homepage_url,
  tosUrl: client.tos_url,
  integration: client.integration?.data?.name || '-',
  company: client.liableCompany?.data?.name || '-',
  defaultRole: client.defaultRole?.data?.display_name || '-',
  defaultFeeStructure: client.defaultFeeStructure?.data?.name || '-',
  adminButtonColor: client.admin_fe_button_color,
  adminButtonText: client.admin_fe_button_text,
  adminButtonTextColor: client.admin_fe_button_text_color,
  disputeEmails: client.dispute_emails
});
export const flatCompany = (company) => ({
  ...company,
  name: company.name.trim() ? company.name : '-',
  google_pay_merchant_id: company.google_pay_merchant_id || '-',
});
export const flatStore = (store) => ({
  ...store,
  google_pay_merchant_id: store?.company?.data?.google_pay_merchant_id || '-',
});
export const flatDispute = (dispute) => ({
  ...dispute,
  amountToDisplay: `${CURRENCY_SYMBOL[dispute.currency]}${priceFromAPI(
    dispute.amount
  )}`,
  feeToDisplay: `${CURRENCY_SYMBOL[dispute.currency]}${priceFromAPI(
    dispute.fee
  )}`,
  status: CUSTOM_DISPUTE_STATUSES[dispute.last_event] ?? dispute.last_event,
  transaction: dispute.transaction?.data || {},
});
